import { useEffect, useState } from "react";
import { Check, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { fetchProfile, updateProfile } from "../lib/api";
import { toast } from "sonner";

export default function Perfil() {
  const { user, isSuperadmin } = useAuth();
  const [profile, setProfile] = useState(null);
  const [nombre, setNombre] = useState("");
  const [idConsultor, setIdConsultor] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile().then((p) => {
      setProfile(p);
      setNombre(p.nombre || "");
      setIdConsultor(p.id_consultor ?? "");
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password && password !== passwordConfirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (password && password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setSaving(true);
    try {
      const payload = { nombre, id_consultor: idConsultor };
      if (password) payload.password = password;
      const updated = await updateProfile(payload);
      setProfile(updated);
      setPassword("");
      setPasswordConfirm("");
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-64 text-[#736F68] text-sm">
        Cargando…
      </div>
    );
  }

  return (
    <div className="px-6 py-8 max-w-lg mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#2D2A26] font-display">Mi perfil</h1>
        <p className="text-sm text-[#736F68] mt-0.5">Configura tu cuenta</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E0D8] p-6">
        {/* Email — read only */}
        <div className="mb-5 pb-5 border-b border-[#E5E0D8]">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] mb-1">
            Email
          </p>
          <p className="text-sm text-[#2D2A26]">{profile.email}</p>
          <p className="text-[11px] text-[#B0A99F] mt-0.5">
            Este email es también el que usas en app.hesttya.pt
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Nombre */}
          <div>
            <label className={labelCls}>Nombre</label>
            <input
              type="text"
              required
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className={inputCls}
              placeholder="Tu nombre completo"
            />
          </div>

          {/* ID Consultor — solo coordinadores */}
          {!isSuperadmin && (
            <div>
              <label className={labelCls}>ID Consultor</label>
              <input
                type="number"
                value={idConsultor}
                onChange={(e) => setIdConsultor(e.target.value)}
                className={inputCls}
                placeholder="Número de consultora en Hesttya"
              />
              <p className="text-[11px] text-[#B0A99F] mt-1">
                Necesario para filtrar los datos de tu equipo. Lo encuentras en tu perfil de app.hesttya.pt.
              </p>
            </div>
          )}

          {/* Contraseña */}
          <div className="border-t border-[#E5E0D8] pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#736F68] mb-3">
              Cambiar contraseña
            </p>
            <p className="text-[11px] text-[#B0A99F] mb-3">
              Actualizar la contraseña aquí también la actualiza para la sincronización con Hesttya.
            </p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Nueva contraseña</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputCls + " pr-10"}
                    placeholder="Dejar vacío para no cambiar"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#736F68] hover:text-[#2D2A26]"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {password && (
                <div>
                  <label className={labelCls}>Confirmar nueva contraseña</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className={inputCls}
                    placeholder="Repite la contraseña"
                    autoComplete="new-password"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-[#C57B67] hover:bg-[#B06A57] disabled:opacity-60 text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors"
            >
              <Check size={15} weight="bold" />
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelCls =
  "block text-xs font-semibold uppercase tracking-wider text-[#736F68] mb-1.5";
const inputCls =
  "w-full border border-[#E5E0D8] rounded-lg px-3 py-2.5 text-sm text-[#2D2A26] bg-white placeholder-[#B0A99F] focus:outline-none focus:ring-2 focus:ring-[#C57B67] focus:border-transparent transition";
